import { useState } from 'react'

import { faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { useBoolean } from 'usehooks-ts'

import { type OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { ButtonSizes, FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { useUnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import { Card } from '@/buyer-portal/components/layouts/CardLayout/card/ui'

import { useManifestTableTracking } from '../../hooks/useManifestTableTracking'
import { MissingDataBanner } from '../../MissingDataBanner'
import { ManifestFullscreenModal } from './ManifestFullscreenModal'
import { ManifestTabs } from './ManifestTabs'
import {
  ControlButton,
  ControlButtonLabel,
  Header,
  ManifestCardTitle,
} from './ui'

export const ListingManifestAgGrid = ({
  isLoggedIn,
  listing,
}: {
  isLoggedIn: boolean
  listing: OrchestratedListingDetails
}) => {
  const { onManifestTableEngaged } = useManifestTableTracking(listing)
  const { formatMessage } = useIntl()
  const { openUnAuthModal } = useUnAuthWarningModal()
  const { toggle: toggleFullscreen, value: isFullscreenOpen } =
    useBoolean(false)
  const [stateLoadTrigger, setStateLoadTrigger] = useState(0)

  const handleFullscreenClick = () => {
    if (!isLoggedIn) {
      openUnAuthModal(
        formatMessage({ id: 'Listing.manifestAgGrid.viewFullScreen' })
      )
      return
    }
    toggleFullscreen()
  }

  const handleFullscreenClose = () => {
    toggleFullscreen()
    setStateLoadTrigger((n) => n + 1)
  }

  if (!listing.lot._id) {
    return (
      <MissingDataBanner
        titleId="Listing.errors.manifestUnavailable.title"
        messageId="Listing.errors.manifestUnavailable.lot"
        log={{
          level: 'warn',
          message: 'ListingManifestAgGrid: listing missing lot id',
          metadata: {
            listingId: listing._id,
            missingField: 'lot._id',
          },
        }}
      />
    )
  }

  return (
    <Card onClick={onManifestTableEngaged}>
      <Header>
        <ManifestCardTitle>
          <FormattedMessage id="Listing.manifestAgGrid.title" />
        </ManifestCardTitle>
        <ControlButton
          $disabled={!isLoggedIn}
          appearance={isLoggedIn ? 'primary' : 'secondary'}
          aria-disabled={!isLoggedIn || undefined}
          onClick={handleFullscreenClick}
          size={ButtonSizes.small}
          title={formatMessage({
            id: 'Listing.manifestAgGrid.searchBar.fullScreen',
          })}
        >
          <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} size="sm" />
          <ControlButtonLabel>
            <FormattedMessage id="Listing.manifestAgGrid.searchBar.fullScreen" />
          </ControlButtonLabel>
        </ControlButton>
      </Header>
      <ManifestTabs
        isLoggedIn={isLoggedIn}
        listing={listing}
        stateLoadTrigger={stateLoadTrigger}
      />
      {isFullscreenOpen && (
        <ManifestFullscreenModal
          listing={listing}
          onClose={handleFullscreenClose}
        />
      )}
    </Card>
  )
}
